import StatusChip from 'components/@extended/StatusChip';
import PinButton from 'components/buttons/PinButton';
import { EllipsisIcon } from 'components/icons';
import KebabMenu, { MenuItem } from 'components/navigation/KebabMenu';
import PageHeader from 'components/PageHeader';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import { ReactNode } from 'react';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { PinKind, recordPin } from 'utils/pins';

/**
 * A label and its value, the value in ink; `label` is a node so SP and RL can carry their full name. The label
 * never breaks, and below `sm` it sits above its value, so every value starts on its label's edge.
 */
export const MetaItem = ({ label, children }: { label: ReactNode; children: ReactNode }) => (
    <span className="flex min-w-0 items-center gap-1.5 max-sm:flex-col max-sm:items-start max-sm:gap-0.5">
        <span className="shrink-0 whitespace-nowrap">{label}</span>
        <span className="text-foreground flex min-w-0 items-center gap-1.5 font-medium">{children}</span>
    </span>
);

/** The meta row under a record's title. */
export const META_ROW_CLASS = 'text-meta text-foreground-tertiary flex w-full flex-wrap items-center gap-x-5 gap-y-1.5';

/** An outlined 36px action beside the primary one, and the ⋯ trigger in the same frame. */
export const OUTLINED_ACTION_CLASS = 'border-border-control bg-card text-foreground h-9 rounded-lg shadow-none';

/** "Open in DMS" in the meta row: today's link, in the document system's blue and the row's type. */
export const DMS_META_CLASS = '[&_a]:text-dms [&_a]:text-meta flex [&_a]:gap-1.5 [&_a]:font-medium [&_svg]:size-3.5';

/** The record's white band around its header. */
export const RECORD_BAND_CLASS = 'bg-card px-(--page-gutter-x) pt-(--page-gutter-y) pb-3';

/**
 * The record page in the new design: the band keeps its place and the panel under it scrolls. Below `sm`, and
 * in a window too short for that split (40rem), a tall band would leave the panel a sliver: there the page
 * scrolls as one and only the tab strip stays, stuck to its top. The scroll padding keeps what a focus brings
 * into view clear of the stuck strip.
 */
export const RECORD_PAGE_CLASS =
    'new:p-0 flex h-full flex-col overflow-hidden max-sm:overflow-y-auto max-sm:scroll-pt-11 [@media(max-height:40rem)]:overflow-y-auto [@media(max-height:40rem)]:scroll-pt-11';

/** Where the page scrolls as one, the tabs and their panel take their content's height and scroll nothing. */
const PAGE_SCROLLS_CLASS =
    'new:max-sm:flex-none new:max-sm:overflow-visible new:[@media(max-height:40rem)]:flex-none new:[@media(max-height:40rem)]:overflow-visible';

/** The tabs under the band, filling what it leaves. Classic's record pages share it, hence the `new:` above. */
export const RECORD_TABS_CLASS = `flex min-h-0 flex-1 flex-col overflow-hidden ${PAGE_SCROLLS_CLASS}`;

/** A tab strip that continues the white band above it, edge to edge; its rule is the band's lower edge. */
export const TAB_STRIP_BAND_CLASS = 'bg-card mb-0 px-(--page-gutter-x)';

/** A record's tab strip: the band's, stuck to the top of the page where the page scrolls as one. */
export const RECORD_TAB_STRIP_CLASS = `${TAB_STRIP_BAND_CLASS} max-sm:sticky max-sm:top-0 max-sm:z-10 [@media(max-height:40rem)]:sticky [@media(max-height:40rem)]:top-0 [@media(max-height:40rem)]:z-10`;

/**
 * Where the page scrolls as one, the panel is a layer of its own under the stuck strip, whatever its content
 * stacks at, and a list's sticky chrome scrolls away with its rows: only the strip stays.
 */
const PANEL_UNDER_STRIP_CLASS =
    'max-sm:isolate max-sm:[--list-chrome-top:auto] [@media(max-height:40rem)]:isolate [@media(max-height:40rem)]:[--list-chrome-top:auto]';

/**
 * The panel under the band, on the canvas: it scrolls, except where the page scrolls as one. It is the containing
 * block of its positioned content, so visually hidden text low in a tab cannot stretch the layout's scroller.
 */
export const RECORD_PANEL_CLASS = `relative min-h-0 flex-1 overflow-y-auto px-(--page-gutter-x) pt-6 pb-(--page-gutter-y) ${PAGE_SCROLLS_CLASS} ${PANEL_UNDER_STRIP_CLASS}`;

/** A row under the strip holding the panel and a column beside it, 24px apart: it fills as the panel alone would. */
export const RECORD_PANEL_ROW_CLASS = `flex min-h-0 flex-1 gap-6 ${PAGE_SCROLLS_CLASS}`;

/** The case's General grid: cards on the left, the narrower column on the right; 3 : 2 from 1024 px, 2 : 1 from 1280 px. */
export const RECORD_GRID_CLASS =
    'grid grid-cols-1 items-start gap-(--card-gap) min-[1280px]:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]';

/** A link in the meta row: ink, as the case's. */
export const META_LINK_CLASS = 'text-foreground w-fit no-underline hover:underline';

/** The amber primary action beside ⋯, sized as the case's Log time. */
export const PRIMARY_ACTION_CLASS = 'h-9 rounded-lg px-3.5 font-semibold';

/** The header's ⋯ menu; nothing when no item applies. */
export const RecordMoreMenu = ({ menuItems }: { menuItems: MenuItem[] }) =>
    menuItems.length > 0 ? (
        <KebabMenu
            menuItems={menuItems}
            icon={<EllipsisIcon className="size-4" />}
            className={cn(OUTLINED_ACTION_CLASS, 'hover:text-foreground size-9 border')}
        />
    ) : null;

/** A record that can be switched off: its state as the header's first chip. */
export const ActiveChip = ({ isActive }: { isActive: boolean }) => (
    <StatusChip type={isActive ? 'success' : 'neutral'} label={t(isActive ? 'active' : 'inactive')} />
);

interface HeaderProps {
    /** Shown in mono before the chips, as the case's number. */
    number?: ReactNode;
    chips?: ReactNode;
    title: string;
    titleNode?: ReactNode;
    /** `MetaItem`s: the meta row under the title. */
    meta?: ReactNode;
    actions?: ReactNode;
    /** The record the star after the chips pins to the sidebar, under `title`, as the case's and the contact's. */
    pin?: { kind: PinKind; id: number };
}

/** A record's header in the new design, laid out as the case's: number and chips, the title, the meta row, actions. */
export const RecordHeader = ({ number, chips, title, titleNode, meta, actions, pin }: HeaderProps) => (
    <div className="flex items-start gap-4">
        <div className="flex min-w-0 flex-auto flex-col gap-2">
            {number || chips || pin ? (
                <div className="flex flex-wrap items-center gap-2.5">
                    {number ? <span className="text-foreground-tertiary text-meta font-mono">{number}</span> : null}
                    <div className="flex flex-wrap items-center gap-1.5">
                        {chips}
                        {pin ? <PinButton pin={recordPin(pin.kind, pin.id, title)} /> : null}
                    </div>
                </div>
            ) : null}
            <PageHeader title={title} titleNode={titleNode} showBreadcrumb>
                {meta ? <div className={META_ROW_CLASS}>{meta}</div> : null}
            </PageHeader>
        </div>
        {actions ? <div className="flex flex-none items-center justify-end gap-2">{actions}</div> : null}
    </div>
);

interface PageProps {
    header: ReactNode;
    /**
     * The tabs, drawn with `RECORD_TABS_CLASS`, `RECORD_TAB_STRIP_CLASS` and `RECORD_PANEL_CLASS`; a record without tabs passes its body
     * with `tabbed={false}`, and the band takes the rule the tab strip would have drawn.
     */
    children: ReactNode;
    tabbed?: boolean;
}

/** The new design's record page: the white band from the header through the tab strip, the panel below. */
export const RecordPage = ({ header, children, tabbed = true }: PageProps) => (
    <PaddedLayout className={RECORD_PAGE_CLASS}>
        <div className={cn(RECORD_BAND_CLASS, !tabbed && 'border-divider border-b pb-5')}>{header}</div>
        {tabbed ? children : <div className={RECORD_PANEL_CLASS}>{children}</div>}
    </PaddedLayout>
);
